import { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  CircularProgress,
  IconButton,
  Avatar,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CampaignIcon from "@mui/icons-material/Campaign";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";
import { tokens } from "../../theme";
import { AD_UPLOAD_ERROR_MESSAGE } from "../../utils/errorMessages";
import { validateForm, RULES } from "../../utils/validators";

const ACCEPTED = ".mp4,.mov,.avi,.jpg,.jpeg,.png,.webp";
const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/x-msvideo"];
const CATEGORIES = [
  "Brand Awareness",
  "Product Launch",
  "Seasonal Offer",
  "Event Promotion",
  "Corporate",
  "Other",
];

// yyyy-mm-dd, matching the `type="date"` input's own value format — used both as
// the native picker's `min` and as the validation floor below, so a date typed in
// (rather than picked) is held to the same rule as one chosen from the calendar.
const todayStr = () => new Date().toLocaleDateString("en-CA");

const UploadAdvertisementModal = ({ open, onClose, onSuccess, editingAd }) => {
  // tokens.* are static `var(--bq-*)` strings whose identity never changes (see
  // theme.js): a theme toggle flips the CSS custom properties on <html>, which
  // the browser repaints without React involvement. So this sx object never
  // needs recomputing on mode change — the old [isDark] dep and its
  // "mutable tokens" comment predated that refactor and were dead weight.
  const fieldSx = useMemo(() => ({}), []);
  const [form, setForm] = useState({
    advertisementName: "",
    description: "",
    category: "",
    startDate: "",
    endDate: "",
    priority: 1,
    assignedRobots: [],
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errors, setErrors] = useState({});
  // The user's Orbit rovers — the only robots an ad can target, since ad
  // playback is an Orbit-exclusive feature (see requireOrbitAccess on the
  // backend). Fetched fresh each time the modal opens rather than once, so a
  // robot newly granted to this user between visits shows up without a reload.
  const [orbitRobots, setOrbitRobots] = useState([]);
  const [robotsLoading, setRobotsLoading] = useState(false);
  const fileInputRef = useRef();
  const dropRef = useRef();

  // Ids from form.assignedRobots that exist in the currently loaded
  // orbitRobots list — see the Select's `value` below for why this matters.
  const selectedRobotIds = useMemo(
    () => form.assignedRobots.filter((id) => orbitRobots.some((r) => r._id === id)),
    [form.assignedRobots, orbitRobots]
  );

  useEffect(() => {
    if (editingAd) {
      setForm({
        advertisementName: editingAd.advertisementName || "",
        description: editingAd.description || "",
        category: editingAd.category || "",
        startDate: editingAd.startDate ? editingAd.startDate.split("T")[0] : "",
        endDate: editingAd.endDate ? editingAd.endDate.split("T")[0] : "",
        priority: editingAd.priority || 1,
        assignedRobots: (editingAd.assignedRobots || []).map((r) =>
          typeof r === "string" ? r : r._id
        ),
      });
    } else {
      setForm({
        advertisementName: "",
        description: "",
        category: "",
        startDate: "",
        endDate: "",
        priority: 1,
        assignedRobots: [],
      });
      setFile(null);
      setPreview(null);
    }
  }, [editingAd, open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const loadOrbitRobots = async () => {
      setRobotsLoading(true);
      try {
        const token = localStorage.getItem("portalToken");
        const res = await apiFetch(`${CONFIG.BASE_URL}/api/portal/robots?roverType=orbit`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) setOrbitRobots(await res.json());
      } catch {
        // Silent — the picker just shows empty; the ad can still be submitted
        // untargeted (platform-wide), same as before this feature existed.
      } finally {
        if (!cancelled) setRobotsLoading(false);
      }
    };
    loadOrbitRobots();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const set = (field) => (e) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
    setErrors((p) => ({ ...p, [field]: "" }));
  };

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    setErrors((p) => ({ ...p, file: "" }));
    if (VIDEO_TYPES.includes(f.type)) {
      setPreview({ type: "video", url: URL.createObjectURL(f), name: f.name, size: f.size });
    } else {
      const reader = new FileReader();
      reader.onload = (ev) =>
        setPreview({ type: "image", url: ev.target.result, name: f.name, size: f.size });
      reader.readAsDataURL(f);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropRef.current.style.borderColor = tokens.borderStrong;
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) handleFile(dropped);
  };

  const validate = () => {
    const e = validateForm(
      {
        advertisementName: { ...RULES.entityName, label: "Advertisement name" },
        description: { ...RULES.description, required: false },
        category: { label: "Category", kind: "name", max: 60 },
        // Priority drives ordering, so a negative or fractional value is
        // meaningless. The field was previously accepted as typed.
        // Bounds match the field's own label and min/max attributes (1–10).
        priority: { label: "Priority", kind: "number", integer: true, minValue: 1, maxValue: 10 },
      },
      form
    );

    // Editing an already-running campaign can leave its startDate in the past
    // (that's the record of when it actually started) — only a NEW start date
    // picked today or later is enforced.
    const today = todayStr();
    if (form.startDate && form.startDate < today) {
      e.startDate = "Start date cannot be in the past";
    }
    if (form.endDate && form.endDate < today) {
      e.endDate = "End date cannot be in the past";
    }
    // A campaign that ends before it starts can never run.
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      e.endDate = "End date must be on or after the start date";
    }

    if (!editingAd && !file) e.file = "Media file is required";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setUploading(true);
    setProgress(0);

    try {
      const token = localStorage.getItem("portalToken");

      if (editingAd) {
        const res = await apiFetch(
          `${CONFIG.BASE_URL}/api/portal/advertisements/${editingAd._id}`,
          {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(form),
          }
        );
        if (!res.ok) {
          const d = await res.json();
          throw new Error(d.message);
        }
      } else {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => {
          if (!v) return;
          // FormData stringifies an array via .toString() (comma-joined ids),
          // which the backend's JSON.parse can't read back as a list — send it
          // JSON-encoded instead, matching parseAssignedRobotsInput's expected shape.
          if (k === "assignedRobots") {
            if (v.length) fd.append(k, JSON.stringify(v));
            return;
          }
          fd.append(k, v);
        });
        fd.append("media", file);

        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
        };
        await new Promise((resolve, reject) => {
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(JSON.parse(xhr.responseText).message || "Upload failed"));
          };
          xhr.onerror = () => reject(new Error("Network error"));
          xhr.open("POST", `${CONFIG.BASE_URL}/api/portal/advertisements`);
          xhr.setRequestHeader("Authorization", `Bearer ${token}`);
          xhr.send(fd);
        });
      }

      onSuccess();
    } catch {
      setErrors((p) => ({ ...p, submit: AD_UPLOAD_ERROR_MESSAGE }));
    } finally {
      setUploading(false);
    }
  };

  const submitLabel = editingAd ? "Save Changes" : "Upload";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: "16px", maxHeight: "90vh" } } }}
    >
      <DialogTitle sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${tokens.border}` }}>
        <Stack direction="row" alignItems="center" gap={1.25}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              background: tokens.accentSoft,
              color: tokens.accent,
            }}
          >
            <CampaignIcon sx={{ fontSize: 16 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: tokens.text }}>
              {editingAd ? "Edit Advertisement" : "Upload Advertisement"}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: tokens.text3 }}>
              {editingAd ? "Update campaign details" : "Submit a campaign for review"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: tokens.text2 }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 2.5,
          pb: 2.25,
          // Extra top clearance: the first field's outlined label shrinks UP out
          // of the input box, so without room here it renders behind the title
          // bar's bottom border.
          //
          // The `&&` is load-bearing. MUI ships
          //   .MuiDialogTitle-root + .MuiDialogContent-root { padding-top: 9px }
          // and that adjacent-sibling selector (specificity 0,2,0) outranks the
          // single generated class this `sx` produces (0,1,0) — so a plain
          // `pt: 5` is silently overridden back to 9px. Doubling the ampersand
          // emits `.css-x.css-x`, which wins at 0,2,0 and lands later in the
          // sheet. Do not "simplify" this to `pt` at the top level.
          "&&": { pt: 2 },
          display: "flex",
          flexDirection: "column",
          gap: 1.75,
        }}
      >
        {errors.submit && (
          <Box sx={{ p: 1.5, borderRadius: "9px", background: tokens.dangerSoft }}>
            <Typography sx={{ fontSize: 12, color: tokens.danger }}>{errors.submit}</Typography>
          </Box>
        )}

        <TextField
          label="Advertisement Name *"
          size="small"
          value={form.advertisementName}
          onChange={set("advertisementName")}
          error={!!errors.advertisementName}
          helperText={errors.advertisementName}
          sx={fieldSx}
        />

        <TextField
          label="Description"
          size="small"
          multiline
          rows={2}
          value={form.description}
          onChange={set("description")}
          error={!!errors.description}
          helperText={errors.description}
          sx={fieldSx}
        />

        <Stack direction="row" gap={1.5}>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: 13 }}>Category</InputLabel>
            <Select
              value={form.category}
              onChange={set("category")}
              label="Category"
              sx={{ borderRadius: "9px", fontSize: 13, background: tokens.surface2 }}
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c} value={c} sx={{ fontSize: 13 }}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Priority (1-10)"
            size="small"
            type="number"
            value={form.priority}
            onChange={set("priority")}
            inputProps={{ min: 1, max: 10 }}
            error={!!errors.priority}
            helperText={errors.priority}
            sx={{ width: 130, ...fieldSx }}
          />
        </Stack>

        <Stack direction="row" gap={1.5}>
          <TextField
            label="Start Date"
            size="small"
            type="date"
            value={form.startDate}
            onChange={set("startDate")}
            InputLabelProps={{ shrink: true }}
            // Editing an already-running ad may show a startDate already in the
            // past — the native picker still floors NEW picks at today, but a
            // date already stored below `min` is left displayed rather than
            // hidden by the browser.
            inputProps={{ min: todayStr() }}
            fullWidth
            error={!!errors.startDate}
            helperText={errors.startDate}
            sx={fieldSx}
          />
          <TextField
            label="End Date"
            size="small"
            type="date"
            value={form.endDate}
            onChange={set("endDate")}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: form.startDate || todayStr() }}
            fullWidth
            error={!!errors.endDate}
            helperText={errors.endDate}
            sx={fieldSx}
          />
        </Stack>

        {/* Robot targeting — which of the user's Orbit rovers should play this
            ad. Left empty, an ad plays platform-wide across every Orbit rover
            the requester's fleet serves (see getAdvertisementFeed on the
            backend), so this field is optional rather than required. */}
        <FormControl size="small" fullWidth error={!!errors.assignedRobots}>
          <InputLabel sx={{ fontSize: 13 }}>Show on robots</InputLabel>
          <Select
            multiple
            // MUI's controlled Select validates `value` against the rendered
            // MenuItems on every render. assignedRobots is seeded from
            // editingAd synchronously, but orbitRobots loads async (and may
            // never include an id whose robot was since unassigned/deleted),
            // so we clamp to ids that actually exist among orbitRobots to
            // avoid an out-of-range value (MUI error #2).
            value={selectedRobotIds}
            onChange={(e) => setForm((p) => ({ ...p, assignedRobots: e.target.value }))}
            input={<OutlinedInput label="Show on robots" />}
            renderValue={(selected) =>
              selected.length
                ? orbitRobots
                    .filter((r) => selected.includes(r._id))
                    .map((r) => r.name)
                    .join(", ")
                : "All robots (no targeting)"
            }
            sx={{ borderRadius: "9px", fontSize: 13, background: tokens.surface2 }}
          >
            {robotsLoading && (
              <MenuItem disabled sx={{ fontSize: 13 }}>
                Loading robots…
              </MenuItem>
            )}
            {!robotsLoading && orbitRobots.length === 0 && (
              <MenuItem disabled sx={{ fontSize: 13 }}>
                No Orbit robots assigned to you
              </MenuItem>
            )}
            {orbitRobots.map((r) => (
              <MenuItem key={r._id} value={r._id} sx={{ fontSize: 13 }}>
                <Checkbox checked={selectedRobotIds.includes(r._id)} size="small" />
                <ListItemText primary={r.name} />
              </MenuItem>
            ))}
          </Select>
          <Typography sx={{ fontSize: 11, color: tokens.text3, mt: 0.5, mx: 1.75 }}>
            Leave empty to show on every robot instead of specific ones.
          </Typography>
        </FormControl>

        {/* File Upload */}
        {!editingAd && (
          <Box>
            {preview ? (
              <Box
                sx={{
                  position: "relative",
                  borderRadius: "10px",
                  overflow: "hidden",
                  border: `1px solid ${tokens.border}`,
                }}
              >
                {preview.type === "image" ? (
                  <img
                    src={preview.url}
                    alt="preview"
                    style={{ width: "100%", maxHeight: 160, objectFit: "cover", display: "block" }}
                  />
                ) : (
                  <Box
                    sx={{
                      p: 3,
                      background: tokens.surface2,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <VideoFileIcon sx={{ fontSize: 32, color: tokens.text2 }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.text }}>
                      {preview.name}
                    </Typography>
                  </Box>
                )}
                <IconButton
                  size="small"
                  onClick={() => {
                    setFile(null);
                    setPreview(null);
                  }}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    background: "rgba(0,0,0,0.6)",
                    color: "#fff",
                    width: 28,
                    height: 28,
                    "&:hover": { background: "rgba(0,0,0,0.8)" },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Box>
            ) : (
              <Box
                ref={dropRef}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  dropRef.current.style.borderColor = tokens.accent;
                }}
                onDragLeave={() => {
                  dropRef.current.style.borderColor = errors.file
                    ? tokens.danger
                    : tokens.borderStrong;
                }}
                onDrop={handleDrop}
                sx={{
                  border: `1.5px dashed ${errors.file ? tokens.danger : tokens.borderStrong}`,
                  borderRadius: "12px",
                  p: "28px 20px",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "0.15s",
                  background: tokens.surface2,
                  "&:hover": { borderColor: tokens.accent },
                }}
              >
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "11px",
                    background: tokens.surface3,
                    color: tokens.accent,
                    mx: "auto",
                    mb: 1.25,
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.text }}>
                  Drag & drop or click to upload
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: tokens.text3, mt: 0.4 }}>
                  MP4, MOV, AVI, JPG, PNG · Video max 100MB, Image max 20MB
                </Typography>
                {errors.file && (
                  <Typography sx={{ fontSize: 11.5, color: tokens.danger, mt: 0.5 }}>
                    {errors.file}
                  </Typography>
                )}
              </Box>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED}
              style={{ display: "none" }}
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </Box>
        )}

        {uploading && (
          <Box>
            <Typography sx={{ fontSize: 12, color: tokens.text2, mb: 0.5 }}>
              Uploading… {progress}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                borderRadius: 4,
                height: 6,
                backgroundColor: tokens.surface3,
                "& .MuiLinearProgress-bar": { background: tokens.accent },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.25, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          disabled={uploading}
          sx={{ borderColor: tokens.border, color: tokens.text }}
        >
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" size="small" disabled={uploading}>
          {uploading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

UploadAdvertisementModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  editingAd: PropTypes.object,
};

export default UploadAdvertisementModal;
